import React, { useState, useEffect } from 'react';
import Axios from "axios";
import { useImmerReducer } from "use-immer";
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {Grid, AppBar, Typography,  Card, CircularProgress,
    Box, Switch, ThemeProvider,  responsiveFontSizes, Checkbox, FormControlLabel, useMediaQuery, useTheme, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem} from "@mui/material";
import YouTubeIcon from '@mui/icons-material/YouTube';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import {createTheme} from '@mui/material/styles';
import "leaflet/dist/leaflet.css";
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import CardComponent from './PiacComponents/CardComponent';
import MapComponent from './PiacComponents/MapComponent';
import MobileDrawer from './MobileDrawer';
import {Icons, RoomIcons} from '../../Utils/IconDisplay.js'; 
import { CategoryLabels as getCategoryLabels, KeyLabels as getKeyLabels,  AdmLabels as getAdmLabels, categories, admlayers, } from '../../Constants/IngatlanOptions.js';

import config, { environment } from '../../config';

let theme = createTheme();
theme = responsiveFontSizes(theme);

function Listings() { 
  const { t } = useTranslation();
  const CategoryLabels = getCategoryLabels(t);
  const KeyLabels = getKeyLabels(t);
  const AdmLabels = getAdmLabels(t);
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const GlobalState = React.useContext(require("../../Contexts/StateContext").default);
  
  const iconMapping = {
    'Hosztel': Icons.hostelIcon,
    'Tombhazlakas': Icons.apartmentIcon,
    'Szallasreggelivel': Icons.bedandbreakfastIcon,
    'udvarhaz': Icons.udvarhazIcon,
    'motel': Icons.bedandbreakfastIcon,
    'Kemping': Icons.campingIcon,
    'Satoreskemping': Icons.tentandcampingIcon,
    'kapolna': Icons.chapelIcon,
    'templom': Icons.churchIcon,
    'var': Icons.varIcon,
    'Borviz': Icons.borvizIcon,
    'Forras': Icons.forrasIcon,
    'termalforras': Icons.hotspringIcon,
    'termalviz': Icons.hotspringIcon,
    'Hotel': Icons.hotelIcon,
    'Hotel***': Icons.hotel3starIcon,
    'Hotel****': Icons.hotel4starIcon,
    'Banya': Icons.mineIcon,
    'Bezartbanya': Icons.mineclosedIcon,
    'Olajesgaz': Icons.oilandgaswellIcon,
    'Kilato': Icons.panoramicviewIcon,
    'Iszapvulkan': Icons.iszapvulkanIcon,
    'Strand': Icons.strandIcon,
    'Uszoda': Icons.uszodaIcon,
    'Sipalya': Icons.sizesIcon,
    'Kastely': Icons.castleIcon,
    'Satortabor': Icons.satrazasIcon,
    'Ingatlan': RoomIcons.RoomIcon,
    'Auto': RoomIcons.RedRoomIcon,
    'Telisportfelszereles': RoomIcons.GreenRoomIcon,
    'Nyarisportfelszereles': RoomIcons.YellowRoomIcon,
    'Sajattermek': RoomIcons.OrangeRoomIcon,
    'Rovasirasepulet': Icons.runestoneIcon,
    'Neprajziepulet': Icons.brokenPotteryIcon,
    'Egyhaziepulet': Icons.churchIcon,
    'Foldtaniepulet': Icons.iszapvulkanIcon,
    'Novenyvilagepulet': Icons.forrasIcon,
    'Asvanyvizepulet': Icons.borvizIcon,
    'Kakasborozda': Icons.kakasborozdaIcon,
    'Letelepedes': Icons.letelepedesIcon,
    'Regesz': Icons.brokenPotteryIcon,
    'Rovasemlek': Icons.runestoneIcon,
  };

  const [selectedCategories, setSelectedCategories] = useState(() => {
    if (location.pathname.includes('/szallasterkep')) {
      return ['Szallas'];
    }
    if (location.pathname.includes('/autoterkep') || 
        location.pathname.includes('/ingatlanterkep') || 
        location.pathname.includes('/telisportfelszereles-terkep') || 
        location.pathname.includes('/nyarisportfelszereles-terkep') || 
        location.pathname.includes('/sajattermeny-terkep')) {
      return ['Piacihirdetesek'];
    }
    return ['Piacihirdetesek'];
  });
  const [selectedAdmLayers, setSelectedAdmLayers] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(() => {
    const isSzallasMap = location.pathname.includes('/szallasterkep');
    const isAutoMap = location.pathname.includes('/autoterkep');
    const isIngatlanMap = location.pathname.includes('/ingatlanterkep');
    const isWinterSportsMap = location.pathname.includes('/telisportfelszereles-terkep');
    const isSummerSportsMap = location.pathname.includes('/nyarisportfelszereles-terkep');
    const isOwnProduceMap = location.pathname.includes('/sajattermeny-terkep');
    const isDedicatedMap = isSzallasMap || isAutoMap || isIngatlanMap || isWinterSportsMap || isSummerSportsMap || isOwnProduceMap;

    const initialSelectedKeys = {};
    Object.keys(categories).forEach(category => {
      initialSelectedKeys[category] = {};
      categories[category].forEach(key => {
        if (isDedicatedMap) {
          if (isSzallasMap && category === 'Szallas') {
            initialSelectedKeys[category][key] = true;
          } else if (isAutoMap && category === 'Piacihirdetesek' && key === 'Auto') {
            initialSelectedKeys[category][key] = true;
          } else if (isIngatlanMap && category === 'Piacihirdetesek' && key === 'Ingatlan') {
            initialSelectedKeys[category][key] = true;
          } else if (isWinterSportsMap && category === 'Piacihirdetesek' && key === 'Telisportfelszereles') {
            initialSelectedKeys[category][key] = true;
          } else if (isSummerSportsMap && category === 'Piacihirdetesek' && key === 'Nyarisportfelszereles') {
            initialSelectedKeys[category][key] = true;
          } else if (isOwnProduceMap && category === 'Piacihirdetesek' && key === 'Sajattermek') {
            initialSelectedKeys[category][key] = true;
          } else {
            initialSelectedKeys[category][key] = false;
          }
        } else {
          if (category === 'Piacihirdetesek') {
            initialSelectedKeys[category][key] = (key === 'Ingatlan' || key === 'Auto');
          } else if (category === 'Latnivalok') {
            initialSelectedKeys[category][key] = false;
          } else if (category === 'Tortenelem' && key === 'Letelepedes') {
            initialSelectedKeys[category][key] = false;
          } else {
            initialSelectedKeys[category][key] = false;
          }
        }
      });
    });
    // Explicitly add initial state for the new building keys in Epitettorokseg if they exist in subKeys
    if (initialSelectedKeys['Epitettorokseg']) {
      ['kapolna', 'templom', 'var', 'kastely', 'udvarhaz'].forEach(key => {
        if (initialSelectedKeys['Epitettorokseg'][key] === undefined) {
           initialSelectedKeys['Epitettorokseg'][key] = false;
        }
      });
    }
    return initialSelectedKeys;
  });  

  const[latitude, setLatitude] = useState(45.8643);
  const[longitude, setLongitude] = useState(25.7848);  

  const [addYoutubeDialogOpen, setAddYoutubeDialogOpen] = useState(false);
  const [newYoutubeLink, setNewYoutubeLink] = useState('');
  const [newYoutubeName, setNewYoutubeName] = useState('');
  const [newYoutubeCategory, setNewYoutubeCategory] = useState('LocalProduct');

  const handleOpenAddYoutube = () => setAddYoutubeDialogOpen(true);
  const handleCloseAddYoutube = () => {
    setAddYoutubeDialogOpen(false);
    setNewYoutubeLink('');
    setNewYoutubeName('');
    setNewYoutubeCategory('LocalProduct');
  };

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function fetchAds() {
      try {
        const response = await Axios.get(config[environment].advertisementsEndpoint, { cancelToken: source.token });
        setAdvertisements(response.data);
      } catch (error) {
        if (!Axios.isCancel(error)) {
          console.error("Error fetching advertisements:", error);
        }
      }
    }
    fetchAds();
    return () => source.cancel();
  }, [config, environment]);

  const getYoutubeThumbnail = (url) => {
    const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return `https://img.youtube.com/vi/${match[2]}/mqdefault.jpg`;
    }
    return 'https://img.youtube.com/vi/fvUnG2jyvOs/mqdefault.jpg'; // Fallback
  };

  const handleAddYoutube = async () => {
    if (!newYoutubeLink || !newYoutubeName) return;

    try {
      await Axios.post(
        config[environment].createAdvertisementEndpoint,
        {
          title: newYoutubeName,
          youtube_link: newYoutubeLink,
          category: newYoutubeCategory
        },
        {
          headers: { Authorization: `Bearer ${GlobalState.jwtAccessToken}` }
        }
      );
      handleCloseAddYoutube();
      // Trigger a refresh of advertisement data
      const response = await Axios.get(config[environment].advertisementsEndpoint);
      setAdvertisements(response.data);
    } catch (error) {
      console.error("Error adding advertisement:", error);
      alert(t('Common.SomethingWentWrong', 'Valami hiba történt.'));
    }
  };

  const initialState = {
      mapInstance: null,
      };
      function ReducerFunction(draft, action){
          switch(action.type){
              case 'getMap':
                  draft.mapInstance = action.mapData;
                  break;
          }
      }

  const [state, dispatch] = useImmerReducer(ReducerFunction, initialState)
  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [flyToLocation, setFlyToLocation] = useState(null);

  const [allListings, setAllListings] = useState([]);  
  const [allBuiltHeritage, setAllBuiltHeritage] = useState([]);
  const [allAccommodation, setAllAccommodation] = useState([]);
  const [allMarketplaceAccommodation, setAllMarketplaceAccommodation] = useState([]);
  const [allSportEquipment, setAllSportEquipment] = useState([]);
  const [allOwnProduce, setAllOwnProduce] = useState([]);
  const [allParishes, setAllParishes] = useState([]);
  const [allTelepulesek, setAllTelepulesek] = useState([]);
  const [allCsangoParishes, setAllCsangoParishes] = useState([]);
  const [allCsangoTelepulesek, setAllCsangoTelepulesek] = useState([]);
  const [allSzekek, setAllSzekek] = useState([]);
  const [allMegyek, setAllMegyek] = useState([]);
  const [allHelynevek, setAllHelynevek] = useState([]);
  const [allFolyovizek, setAllFolyovizek] = useState([]);
  const [allHistory, setAllHistory] = useState({});
  const [allMineralWater, setAllMineralWater] = useState({});
  const [allSport, setAllSport] = useState({});
  const [allTouristRoutes, setAllTouristRoutes] = useState({ tourist_routes: [], marked_paths: [] });
  const [showTouristRoutes, setShowTouristRoutes] = useState(false);
  const [showCsango, setShowCsango] = useState(false);
  const [advertisements, setAdvertisements] = useState([]);
  
 
 

 

  useEffect(() => {
    const source = Axios.CancelToken.source();

    async function GetAllListings() {
      const isIngatlanSelected = selectedKeys['Piacihirdetesek']['Ingatlan'];
      const isAutoSelected = selectedKeys['Piacihirdetesek']['Auto'];
      const isWinterSportsSelected = selectedKeys['Piacihirdetesek']['Telisportfelszereles'];
      const isSummerSportsSelected = selectedKeys['Piacihirdetesek']['Nyarisportfelszereles'];
      const isOwnProduceSelected = selectedKeys['Piacihirdetesek']['Sajattermek'];

      const isRovasbuildingSelected = selectedKeys['Latnivalok']?.['Rovasirasepulet'];
      const isNeprajzbuildingSelected = selectedKeys['Latnivalok']?.['Neprajziepulet'];
      const isEgyhazbuildingSelected = selectedKeys['Latnivalok']?.['Egyhaziepulet'];
      const isFoldtanbuildingSelected = selectedKeys['Latnivalok']?.['Foldtaniepulet'];
      const isNovenybuildingSelected = selectedKeys['Latnivalok']?.['Novenyvilagepulet'];
      const isAsvanyvizbuildingSelected = selectedKeys['Latnivalok']?.['Asvanyvizepulet'];

      let combinedListings = [];

      // 1. Fetch Real Estate (Ingatlan)
      if (isIngatlanSelected) {
        try {
          const response = await Axios.get(config[environment].propertiesEndpoint, { cancelToken: source.token });
          combinedListings = [...combinedListings, ...response.data.map(l => ({ ...l, key: 'Property' }))];
        } catch (error) {
          if (!Axios.isCancel(error)) console.error("Error fetching properties:", error);
        }
      }

      // 2. Fetch Cars (Auto)
      if (isAutoSelected) {
        try {
          const response = await Axios.get(config[environment].carsEndpoint, { cancelToken: source.token });
          combinedListings = [...combinedListings, ...response.data.map(l => ({ ...l, key: 'Auto' }))];
        } catch (error) {
          if (!Axios.isCancel(error)) console.error("Error fetching cars:", error);
        }
      }

      // 3. Fetch Sport Equipment (Winter/Summer)
      if (isWinterSportsSelected || isSummerSportsSelected) {
        try {
          const endpoint = config[environment].sportEquipmentEndpoint;
          const response = await Axios.get(endpoint, { cancelToken: source.token });
          const winter = response.data.filter(l => l.season === 'Winter' && isWinterSportsSelected).map(l => ({ ...l, key: 'Telisportfelszereles', picture1: l.picture }));
          const summer = response.data.filter(l => l.season === 'Summer' && isSummerSportsSelected).map(l => ({ ...l, key: 'Nyarisportfelszereles', picture1: l.picture }));
          combinedListings = [...combinedListings, ...winter, ...summer];
        } catch (error) {
          if (!Axios.isCancel(error)) console.error("Error fetching sport equipment:", error);
        }
      }

      // 4. Fetch Own Produce
      if (isOwnProduceSelected) {
        try {
          const endpoint = config[environment].ownProduceEndpoint;
          const response = await Axios.get(endpoint, { cancelToken: source.token });
          combinedListings = [...combinedListings, ...response.data.map(l => ({ ...l, key: 'Sajattermek', picture1: l.picture }))];
        } catch (error) {
          if (!Axios.isCancel(error)) console.error("Error fetching own produce:", error);
        }
      }

      // 5. Fetch Built Heritage sub-layers if selected
      if (isRovasbuildingSelected || isNeprajzbuildingSelected || isEgyhazbuildingSelected || 
          isFoldtanbuildingSelected || isNovenybuildingSelected || isAsvanyvizbuildingSelected) {
          
          if (isRovasbuildingSelected) {
              try {
                  const endpoint = config[environment].historyEndpoint;
                  const resp = await Axios.get(endpoint, { cancelToken: source.token });
                  combinedListings = [...combinedListings, ...(resp.data.Rovasemlek || []).map(item => ({ ...item, key: 'Rovasirasepulet', picture1: item.picture1 }))];
              } catch (e) { if (!Axios.isCancel(e)) console.error(e); }
          }
          if (isNeprajzbuildingSelected) {
              try {
                  const endpoint = config[environment].historyEndpoint;
                  const resp = await Axios.get(endpoint, { cancelToken: source.token });
                  combinedListings = [...combinedListings, ...(resp.data.Regesz || []).map(item => ({ ...item, key: 'Neprajziepulet', picture1: item.picture1 }))];
              } catch (e) { if (!Axios.isCancel(e)) console.error(e); }
          }
          if (isEgyhazbuildingSelected) {
              try {
                  const endpoint = config[environment].builtHeritageEndpoint;
                  const resp = await Axios.get(endpoint, { cancelToken: source.token });
                  combinedListings = [...combinedListings, ...(resp.data.templom || []).map(item => ({ ...item, key: 'Egyhaziepulet', picture1: item.picture1 }))];
              } catch (e) { if (!Axios.isCancel(e)) console.error(e); }
          }
          if (isFoldtanbuildingSelected) {
              try {
                  const endpoint = config[environment].geographicEndpoint + "?type=geological";
                  const resp = await Axios.get(endpoint, { cancelToken: source.token });
                  combinedListings = [...combinedListings, ...(resp.data || []).map(item => ({ ...item, key: 'Foldtaniepulet', picture1: item.picture1 }))];
              } catch (e) { if (!Axios.isCancel(e)) console.error(e); }
          }
          if (isNovenybuildingSelected) {
              try {
                  const endpoint = config[environment].geographicEndpoint + "?type=botanical";
                  const resp = await Axios.get(endpoint, { cancelToken: source.token });
                  combinedListings = [...combinedListings, ...(resp.data || []).map(item => ({ ...item, key: 'Novenyvilagepulet', picture1: item.picture1 }))];
              } catch (e) { if (!Axios.isCancel(e)) console.error(e); }
          }
          if (isAsvanyvizbuildingSelected) {
              try {
                  const endpoint = config[environment].mineralWaterEndpoint;
                  const resp = await Axios.get(endpoint, { cancelToken: source.token });
                  let combined = [];
                  Object.values(resp.data).forEach(list => { combined = [...combined, ...list]; });
                  combinedListings = [...combinedListings, ...combined.map(item => ({ ...item, key: 'Asvanyvizepulet', picture1: item.picture1 }))];
              } catch (e) { if (!Axios.isCancel(e)) console.error(e); }
          }
      }

      setAllListings(combinedListings);
      setDataIsLoading(false);

      if (flyToLocation && state.mapInstance) {
        state.mapInstance.flyTo(flyToLocation, 18);
        setFlyToLocation(null);
      }
    }
    GetAllListings();

    return () => {
      source.cancel();
    };
  }, [config, environment, flyToLocation, state.mapInstance, selectedKeys['Piacihirdetesek'], selectedKeys['Latnivalok']]);
  
  useEffect(() => {
    const source = Axios.CancelToken.source();
  
    async function GetAllBuiltHeritage() {
      if (selectedCategories.includes('Epitettorokseg')) { 
        try {
          const BuiltHeritageEndpoint = config[environment].builtHeritageEndpoint;
          const response = await Axios.get(BuiltHeritageEndpoint, { cancelToken: source.token });
          const fetchedData = response.data;
          const filteredData = {};

          Object.keys(fetchedData).forEach(key => {
            if (selectedKeys['Epitettorokseg'][key]) {
              filteredData[key] = fetchedData[key];
            } else {
              filteredData[key] = [];
            }
          });

          setAllBuiltHeritage(filteredData);
          setDataIsLoading(false);
          
          if (flyToLocation && state.mapInstance) {
            state.mapInstance.flyTo(flyToLocation, 16);
            setFlyToLocation(null);
          }
        } catch (error) {
        }
      }
    }
    GetAllBuiltHeritage();
    return () => {
      source.cancel();
    };
  }, [config, environment, flyToLocation, state.mapInstance, selectedCategories, selectedKeys['Epitettorokseg']]);
  
  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function GetAllAccommodation() {
      if (selectedCategories.includes('Szallas')) { // Check if Accommodation category is selected
        try {
          const AccommodationEndpoint = config[environment].accommodationEndpoint; 
          const response = await Axios.get(AccommodationEndpoint, { cancelToken: source.token });
          const fetchedData = response.data;
          const filteredData = {};

          Object.keys(fetchedData).forEach(key => {
            if (selectedKeys['Szallas'][key]) {
              filteredData[key] = fetchedData[key];
            } else {
              filteredData[key] = [];
            }
          });

          setAllAccommodation(filteredData);
          setDataIsLoading(false);
          
          if (flyToLocation && state.mapInstance) {
            state.mapInstance.flyTo(flyToLocation, 16);
            setFlyToLocation(null);
          }
        } catch (error) {
          console.log(error.response);
        }
      }
    }
    GetAllAccommodation();
    return () => {
      source.cancel();
    };
  }, [config, environment, flyToLocation, state.mapInstance, selectedCategories, selectedKeys['Szallas']]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function GetAllMarketplaceAccommodation() {
      if (selectedCategories.includes('Szallas')) {
        try {
          const endpoint = config[environment].marketplaceAccommodationEndpoint;
          const response = await Axios.get(endpoint, { cancelToken: source.token });
          setAllMarketplaceAccommodation(response.data);
        } catch (error) {
          if (!Axios.isCancel(error)) {
            console.error("Error fetching marketplace accommodation:", error);
          }
        }
      } else {
        setAllMarketplaceAccommodation([]);
      }
    }
    GetAllMarketplaceAccommodation();
    return () => source.cancel();
  }, [config, environment, selectedCategories]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function GetAllHistory() {
      if (selectedCategories.includes('Tortenelem')) {
        try {
          const HistoryEndpoint = config[environment].historyEndpoint;
          const response = await Axios.get(HistoryEndpoint, { cancelToken: source.token });
          
          const fetchedData = response.data;
          const filteredData = {};
          
          Object.keys(fetchedData).forEach(key => {
            if (selectedKeys['Tortenelem'][key]) {
              filteredData[key] = fetchedData[key];
            } else {
              filteredData[key] = [];
            }
          });
          
          setAllHistory(filteredData);
          setDataIsLoading(false);
        } catch (error) {
          console.log(error.response);
        }
      }
    }
    GetAllHistory();
    return () => source.cancel();
  }, [config, environment, selectedCategories, selectedKeys['Tortenelem']]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function GetAllMineralWater() {
      if (selectedCategories.includes('Asvanyviz')) {
        try {
          const MineralWaterEndpoint = config[environment].mineralWaterEndpoint;
          const response = await Axios.get(MineralWaterEndpoint, { cancelToken: source.token });
          
          const fetchedData = response.data;
          const filteredData = {};
          
          Object.keys(fetchedData).forEach(key => {
            if (selectedKeys['Asvanyviz'][key]) {
              filteredData[key] = fetchedData[key];
            } else {
              filteredData[key] = [];
            }
          });
          
          setAllMineralWater(filteredData);
          setDataIsLoading(false);
        } catch (error) {
          console.log(error.response);
        }
      }
    }
    GetAllMineralWater();
    return () => source.cancel();
  }, [config, environment, selectedCategories, selectedKeys['Asvanyviz']]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function GetAllSport() {
      if (selectedCategories.includes('Sport')) {
        try {
          const SportEndpoint = config[environment].sportEndpoint;
          const response = await Axios.get(SportEndpoint, { cancelToken: source.token });
          
          const fetchedData = response.data;
          const filteredData = {};
          
          Object.keys(fetchedData).forEach(key => {
            // Map backend keys to frontend keys if they differ
            let frontendKey = key;
            if (key === 'SkiArea' || key === 'SkiLine') {
                frontendKey = 'Sipalya';
            } else if (key === 'StrandUszoda') {
                // We'll map StrandUszoda to both if we want it to show up for either
                // but since it's a point, we might need to be careful.
                // For now, let's just use the key if it matches.
                frontendKey = 'Strand'; // Default to Strand for StrandUszoda points
            }

            if (selectedKeys['Sport'][frontendKey]) {
              if (!filteredData[frontendKey]) filteredData[frontendKey] = [];
              filteredData[frontendKey] = [...filteredData[frontendKey], ...fetchedData[key]];
            } else {
              if (!filteredData[frontendKey]) filteredData[frontendKey] = [];
            }
          });

          setAllSport(filteredData);
          setDataIsLoading(false);
        } catch (error) {
          console.log(error.response);
        }
      }
    }
    GetAllSport();
    return () => source.cancel();
  }, [config, environment, selectedCategories, selectedKeys['Sport']]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function GetAllGeographic() {
      if (selectedAdmLayers.includes('folyovizek')) {
        try {
          const GeographicEndpoint = config[environment].geographicEndpoint;
          const response = await Axios.get(GeographicEndpoint, { cancelToken: source.token });
          setAllFolyovizek(response.data.Folyovizek);
          setDataIsLoading(false);
        } catch (error) {
          console.log(error.response);
        }
      }
    }
    GetAllGeographic();
    return () => source.cancel();
  }, [config, environment, selectedAdmLayers]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function getAllCsangoParishes(page = 1) {
      if (showCsango && selectedAdmLayers.includes('kozsegvaros')) {
        try {
          const endpoint = config[environment].csangoKozsegVarosEndpoint(page);
          const response = await Axios.get(endpoint, { cancelToken: source.token });
          const data = response.data.results || response.data || [];
          if (Array.isArray(data)) {
            setAllCsangoParishes(prev => page === 1 ? data : [...prev, ...data]);
          }
          if (response.data.next) {
            getAllCsangoParishes(page + 1);
          }
          setDataIsLoading(false);
        } catch (error) {
          console.log(error);
        }
      } else {
        setAllCsangoParishes([]);
      }
    }
    getAllCsangoParishes();
    return () => source.cancel();
  }, [config, environment, selectedAdmLayers, showCsango]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function getAllCsangoTelepulesek(page = 1) {
      if (showCsango && selectedAdmLayers.includes('telepules')) {
        try {
          const endpoint = config[environment].csangoTelepulesEndpoint(page);
          const response = await Axios.get(endpoint, { cancelToken: source.token });
          const data = response.data.results || response.data || [];
          if (Array.isArray(data)) {
            setAllCsangoTelepulesek(prev => page === 1 ? data : [...prev, ...data]);
          }
          if (response.data.next) {
            getAllCsangoTelepulesek(page + 1);
          }
          setDataIsLoading(false);
        } catch (error) {
          console.log(error);
        }
      } else {
        setAllCsangoTelepulesek([]);
      }
    }
    getAllCsangoTelepulesek();
    return () => source.cancel();
  }, [config, environment, selectedAdmLayers, showCsango]);


  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function getAllTelepulesek(page = 1) {
      if (selectedAdmLayers.includes('telepules')) {
        try {
          const TelepulesEndpoint = config[environment].telepulesEndpoint(page);
          const response = await Axios.get(TelepulesEndpoint, { cancelToken: source.token });
          const telepulesData = Array.isArray(response.data) ? response.data : (response.data.results || []);
          if (Array.isArray(telepulesData)) {
            setAllTelepulesek(prevTelepulesek => [...prevTelepulesek, ...telepulesData]);
          }
          setDataIsLoading(false);
          if (flyToLocation && state.mapInstance) {
            state.mapInstance.flyTo(flyToLocation, 16);
            setFlyToLocation(null);
          }
          const next = response.data.next;
          if (next) {
            // Extract page number from next URL or just use page + 1
            getAllTelepulesek(page + 1);
          }
        } catch (error) {
          if (Axios.isCancel(error)) {
            console.log('Request cancelled', error.message);
          } else {
            console.log(error.response);
          }
        }
      } else {
        setAllTelepulesek([]);
      }
    }
    getAllTelepulesek();
    return () => {
      source.cancel();
    };
  }, [config, environment, flyToLocation, state.mapInstance, selectedAdmLayers]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function GetAllParishes(page = 1) {
      if (selectedAdmLayers.includes('kozsegvaros')) {
        try {
          const ParishEndpoint = config[environment].parishEndpoint(page);
          const response = await Axios.get(ParishEndpoint, { cancelToken: source.token });
          const parishData = Array.isArray(response.data) ? response.data : (response.data.results || []);
          if (Array.isArray(parishData)) {
            setAllParishes(prevParishes => [...prevParishes, ...parishData]);
          }
          setDataIsLoading(false);
          if (flyToLocation && state.mapInstance) {
            state.mapInstance.flyTo(flyToLocation, 16);
            setFlyToLocation(null);
          }
          if (response.data.next) {
            GetAllParishes(page + 1);
          }
        } catch (error) {
          if (Axios.isCancel(error)) {
            console.log('Request cancelled', error.message);
          } else {
            console.log(error.response);
          }
        }
      } else {
        setAllParishes([]);
      }
    }
    GetAllParishes();
    return () => {
      source.cancel();
    };
  }, [config, environment, flyToLocation, state.mapInstance, selectedAdmLayers]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function getAllSzekek(page = 1) {
      if (selectedAdmLayers.includes('szek')) {
        try {
          const SzekEndpoint = config[environment].szekEndpoint(page);
          const response = await Axios.get(SzekEndpoint, { cancelToken: source.token });
          const szekData = Array.isArray(response.data) ? response.data : (response.data.results || []);
          if (Array.isArray(szekData)) {
            setAllSzekek(prevSzekek => [...prevSzekek, ...szekData]);
          }
          setDataIsLoading(false);
          if (flyToLocation && state.mapInstance) {
            state.mapInstance.flyTo(flyToLocation, 16);
            setFlyToLocation(null);
          }
          if (response.data.next) {
            getAllSzekek(page + 1);
          }
        } catch (error) {
          if (Axios.isCancel(error)) {
            console.log('Request cancelled', error.message);
          } else {
            console.log(error.response);
          }
        }
      } else {
        setAllSzekek([]);
      }
    }
    getAllSzekek();
    return () => {
      source.cancel();
    };
  }, [config, environment, flyToLocation, state.mapInstance, selectedAdmLayers]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function getAllMegyek(page = 1) {
      if (selectedAdmLayers.includes('megye')) {
        try {
          const MegyeEndpoint = config[environment].megyeEndpoint(page);
          const response = await Axios.get(MegyeEndpoint, { cancelToken: source.token });
          const megyeData = Array.isArray(response.data) ? response.data : (response.data.results || []);
          if (Array.isArray(megyeData)) {
            setAllMegyek(prevMegyek => [...prevMegyek, ...megyeData]);
          }
          setDataIsLoading(false);
          if (flyToLocation && state.mapInstance) {
            state.mapInstance.flyTo(flyToLocation, 16);
            setFlyToLocation(null);
          }
          if (response.data.next) {
            getAllMegyek(page + 1);
          }
        } catch (error) {
          if (Axios.isCancel(error)) {
            console.log('Request cancelled', error.message);
          } else {
            console.log(error.response);
          }
        }
      } else {
        setAllMegyek([]);
      }
    }
    getAllMegyek();
    return () => {
      source.cancel();
    };
  }, [config, environment, flyToLocation, state.mapInstance, selectedAdmLayers]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function getAllHelynevek(page = 1) {
      if (selectedAdmLayers.includes('helynev')) {
        try {
          const HelynevEndpoint = config[environment].helynevEndpoint(page);
          const response = await Axios.get(HelynevEndpoint, { cancelToken: source.token });
          if (Array.isArray(response.data.results)) {
            setAllHelynevek(prevHelynevek => [...prevHelynevek, ...response.data.results]);
          }
          setDataIsLoading(false);
          if (flyToLocation && state.mapInstance) {
            state.mapInstance.flyTo(flyToLocation, 16);
            setFlyToLocation(null);
          }
          if (response.data.next) {
            getAllHelynevek(page + 1);
          }
        } catch (error) {
          if (Axios.isCancel(error)) {
            console.log('Request cancelled', error.message);
          } else {
            console.log(error.response);
          }
        }
      } else {
        setAllHelynevek([]);
      }
    }
    getAllHelynevek();
    return () => {
      source.cancel();
    };
  }, [config, environment, flyToLocation, state.mapInstance, selectedAdmLayers]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function GetAllTouristRoutes() {
      if (showTouristRoutes) {
        try {
          const endpoint = config[environment].touristRoutesEndpoint;
          const response = await Axios.get(endpoint, { cancelToken: source.token });
          setAllTouristRoutes(response.data);
        } catch (error) {
          if (!Axios.isCancel(error)) {
            console.error("Error fetching tourist routes:", error);
          }
        }
      } else {
        setAllTouristRoutes({ tourist_routes: [], marked_paths: [] });
      }
    }
    GetAllTouristRoutes();
    return () => source.cancel();
  }, [config, environment, showTouristRoutes]);

  if(dataIsLoading === true){
    return (<Grid container justifyContent="center" alignItems="center" sx={{height:"100vh"}}>
      <CircularProgress/>
    </Grid>);
  }

  const handleCategoryToggle = (category) => {
    const isSelected = selectedCategories.includes(category);    
    setSelectedCategories(prevSelected => {
      if (isSelected) {
        // Remove all layers belonging to the category
        if (category === 'Piacihirdetesek') {
          setAllListings(prev => prev.filter(l => !['Ingatlan', 'Auto', 'Telisportfelszereles', 'Nyarisportfelszereles', 'Sajattermek'].includes(l.key)));
        } else if (category === 'Latnivalok') {
          setAllListings(prev => prev.filter(l => !['Rovasirasepulet', 'Neprajziepulet', 'Egyhaziepulet', 'Foldtaniepulet', 'Novenyvilagepulet', 'Asvanyvizepulet'].includes(l.key)));
        } else if (category === 'Epitettorokseg') {
          setAllBuiltHeritage([]);
        } else if (category === 'Szallas') {
          setAllAccommodation([]);
        } else if (category === 'Tortenelem') {
          setAllHistory({});
        } else if (category === 'Asvanyviz') {
          setAllMineralWater({});
        } else if (category === 'Sport') {
          setAllSport({});
        }
      }
      return isSelected ? prevSelected.filter(cat => cat !== category) : [...prevSelected, category];
    });
    // Toggle all keys of the category
    setSelectedKeys(prevSelected => {
      const updatedSelectedKeys = { ...prevSelected };
      Object.keys(prevSelected[category]).forEach(key => {
        if (category === 'Tortenelem' && key === 'Letelepedes' && !isSelected) {
          // If we are turning ON the category, keep Letelepedes OFF
          updatedSelectedKeys[category][key] = false;
        } else {
          updatedSelectedKeys[category][key] = !isSelected;
        }
      });
      return updatedSelectedKeys;
    });
  }; 

  const handleKeyToggle = (category) => (key) => {
    setSelectedKeys(prevSelected => {
      const isSelected = prevSelected[category][key];
      const updatedSelectedKeys = {
        ...prevSelected,
        [category]: {
          ...prevSelected[category],
          [key]: !isSelected
        }
      };
      // If the key is currently selected, clear the related data
      if (isSelected) {
        if (category === 'Piacihirdetesek' || category === 'Latnivalok') {
          setAllListings(prevListings => {
            return prevListings.filter(listing => listing.key !== key);
          });
        } else if (category === 'Epitettorokseg') {
          setAllBuiltHeritage(prevHeritage => {
            const updatedHeritage = { ...prevHeritage };
            updatedHeritage[key] = [];
            return updatedHeritage;
          });
        } else if (category === 'Szallas') {
          setAllAccommodation(prevAccommodation => {
            const updatedAccommodation = { ...prevAccommodation };
            updatedAccommodation[key] = [];
            return updatedAccommodation;
          });
        } else if (category === 'Tortenelem') {
          setAllHistory(prevHistory => {
            const updatedHistory = { ...prevHistory };
            updatedHistory[key] = [];
            return updatedHistory;
          });
        } else if (category === 'Asvanyviz') {
          setAllMineralWater(prevMineral => {
            const updatedMineral = { ...prevMineral };
            updatedMineral[key] = [];
            return updatedMineral;
          });
        } else if (category === 'Sport') {
          setAllSport(prevSport => {
            const updatedSport = { ...prevSport };
            updatedSport[key] = [];
            return updatedSport;
          });
        }
      } else {
        // If the key is not currently selected, repopulate the related data from the API
        // This is handled by the useEffects that depend on selectedKeys
      }
      return updatedSelectedKeys;
    });
  };

   
  const handleAdmLayerToggle = (admlayer) => {
    setSelectedAdmLayers(prevSelectedAdmLayers => {
      if (prevSelectedAdmLayers.includes(admlayer)) {
        // If the admlayer is already selected, remove it from the array
        return prevSelectedAdmLayers.filter(parish => parish !== admlayer);
      } else {
        // If the admlayer is not selected, add it to the array
        return [...prevSelectedAdmLayers, admlayer];
      }
    });    
  };
  
  const isSzallasMap = location.pathname.includes('/szallasterkep');
  const isAutoMap = location.pathname.includes('/autoterkep');
  const isIngatlanMap = location.pathname.includes('/ingatlanterkep');
  const isWinterSportsMap = location.pathname.includes('/telisportfelszereles-terkep');
  const isSummerSportsMap = location.pathname.includes('/nyarisportfelszereles-terkep');
  const isOwnProduceMap = location.pathname.includes('/sajattermeny-terkep');
  const isDedicatedMap = isSzallasMap || isAutoMap || isIngatlanMap || isWinterSportsMap || isSummerSportsMap || isOwnProduceMap;

  return (
    <ThemeProvider theme={theme}>
  <Grid container sx={{ position: 'relative', width: '100%', ml: 0 }}>
    {!isMobile && (
      <Grid item xs={12} md={isAutoMap ? 6 : 4} sx={{ height: 'calc(100vh - 64px - 50px)', display: 'flex', flexDirection: 'column', zIndex: 1100, mt: '114px' }}>
        <Box className="cards-grid" sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 1 }}>
          {/* YouTube szekció */}
          {!isDedicatedMap && (
            <Box sx={{ mb: 1, flex: '0 0 auto', width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                  Szolgálati közlemények | Zandírhám Clearwater Revival | Reklám
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<YouTubeIcon />}
                    href="https://www.youtube.com/@Zandírhám"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textTransform: 'none', ml: 0.5, height: '24px', fontSize: '0.7rem' }}
                  >
                    YouTube
                  </Button>
                </Box>
              </Box>
              
              {/* Első sor - Zandírhám videók */}
              <Grid container spacing={1} sx={{ mb: 1 }}>
                {[
                  { id: '1', title: 'Zandírhám Clearwater Revival', thumbnail: 'https://img.youtube.com/vi/fvUnG2jyvOs/mqdefault.jpg', link: 'https://www.youtube.com/watch?v=fvUnG2jyvOs' },
                ].map((video) => (
                  <Grid item xs={4} key={video.id}>
                    <a href={video.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'block', lineHeight: 0 }}>
                      <Box
                        component="img"
                        src={video.thumbnail}
                        alt={video.title}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          aspectRatio: '16 / 9',
                          objectFit: 'cover',
                          borderRadius: 1,
                          cursor: 'pointer',
                          display: 'block',
                          '&:hover': { opacity: 0.8 }
                        }}
                      />
                    </a>
                  </Grid>
                ))}
              </Grid>

              {/* Második sor - Reklám videók */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                 <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                   {t('Advertisement.SecondRowTitle', 'Partner reklámvideók')}
                 </Typography>
                 <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={t('Advertisement.AddLink', 'Reklám link hozzáadása')}>
                    <IconButton size="small" color="primary" onClick={handleOpenAddYoutube} sx={{ p: 0, mr: 0.5 }}>
                      <AddCircleOutlineIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                  <Button 
                    size="small" 
                    variant="text" 
                    endIcon={<ArrowForwardIcon sx={{ fontSize: '0.8rem' }} />} 
                    onClick={() => navigate('/reklam')}
                    sx={{ fontSize: '0.65rem', textTransform: 'none', p: 0 }}
                  >
                    {t('Advertisement.SeeAll', 'Összes reklám')}
                  </Button>
                 </Box>
              </Box>
              <Grid container spacing={1}>
                {advertisements.slice(0, 3).map((ad) => (
                  <Grid item xs={4} key={ad.id}>
                    <a href={ad.youtube_link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'block', lineHeight: 0 }}>
                      <Box
                        component="img"
                        src={getYoutubeThumbnail(ad.youtube_link)}
                        alt={ad.title}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          aspectRatio: '16 / 9',
                          objectFit: 'cover',
                          borderRadius: 1,
                          cursor: 'pointer',
                          display: 'block',
                          '&:hover': { opacity: 0.8 }
                        }}
                      />
                    </a>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Szállások szekció (Marketplace) */}
          {isSzallasMap && (
            <Box className="scrollable-section" sx={{ flex: 1 }}>
              <Box className="section-header">
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{t('Header.Szallas')}</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate('/accommodation-market')}
                  sx={{ textTransform: 'none' }}
                >
                  {t('Map.All')}
                </Button>
              </Box>
              <Box sx={{ p: 1 }}>
                <Grid container spacing={1}>
                  {allMarketplaceAccommodation.map((listing) => (
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={6} key={listing.id}>
                      <CardComponent listing={{...listing, key: 'Szallas', isMarketplace: true}} state={state} setFlyToLocation={setFlyToLocation} navigate={navigate} />
                    </Grid>
                  ))}
                  {Object.values(allAccommodation).flat().map((listing) => (
                    <Grid item xs={12} sm={12} md={12} lg={12} xl={6} key={listing.id}>
                      <CardComponent listing={{...listing, key: 'Szallas'}} state={state} setFlyToLocation={setFlyToLocation} navigate={navigate} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          )}

          {/* Ingatlanok szekció */}
          {(!isDedicatedMap || isIngatlanMap) && (
            <Box className="scrollable-section" sx={{ flex: 1 }}>
              <Box className="section-header">
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{t('Map.Properties')}</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate('/all-property-listings')}
                  sx={{ textTransform: 'none' }}
                >
                  {t('Map.All')}
                </Button>
              </Box>
              <Box sx={{ p: 1 }}>
                <Grid container spacing={1}>
                  {allListings.filter(l => l.key === 'Ingatlan').map((listing) => {
                    return (
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={6} key={listing.id}>
                         <CardComponent listing={listing} state={state} setFlyToLocation={setFlyToLocation} navigate={navigate} />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
          )}

          {/* Autók szekció */}
          {(!isDedicatedMap || isAutoMap) && (
            <Box className="scrollable-section" sx={{ flex: 1 }}>
              <Box className="section-header">
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{t('Map.Cars')}</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate('/all-car-listings')}
                  sx={{ textTransform: 'none' }}
                >
                  {t('Map.All')}
                </Button>
              </Box>
              <Box sx={{ p: 1 }}>
                <Grid container spacing={1}>
                  {allListings.filter(l => l.key === 'Auto').map((listing) => {
                    return (
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={6} key={listing.id}>
                         <CardComponent listing={listing} state={state} setFlyToLocation={setFlyToLocation} navigate={navigate} />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
          )}

          {/* Other listings if any */}
          {(!isDedicatedMap || isWinterSportsMap || isSummerSportsMap || isOwnProduceMap) && (
            <Box sx={{ flex: '0 0 auto', mb: 0, width: '100%', minHeight: 'auto' }}>
              <Box sx={{ p: 1 }}>
                <Grid container spacing={1}>
                  {allListings.filter(l => {
                    if (isWinterSportsMap) return l.key === 'Telisportfelszereles';
                    if (isSummerSportsMap) return l.key === 'Nyarisportfelszereles';
                    if (isOwnProduceMap) return l.key === 'Sajattermek';
                    return l.key !== 'Ingatlan' && l.key !== 'Auto';
                  }).map((listing) => {
                    return (
                      <Grid item xs={12} sm={12} md={12} lg={12} xl={6} key={listing.id}>
                        <CardComponent listing={listing} state={state} setFlyToLocation={setFlyToLocation} navigate={navigate} />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
    )}
    {/* Map */}
    <Grid item xs={12} md={isAutoMap ? 6 : (isDedicatedMap ? 12 : 8)} sx={{ 
      overflow: 'hidden', 
      height: { xs: 'calc(100vh - 64px)', md: isDedicatedMap ? 'calc(100vh - 64px - 50px)' : 'calc(100vh - 64px - 50px)' }, 
      position: 'relative', 
      mt: { xs: 0, md: '114px' },
      display: 'flex',
      justifyContent: 'center'
    }}>
      {/* Add YouTube Dialog */}
      <Dialog open={addYoutubeDialogOpen} onClose={handleCloseAddYoutube}>
        <DialogTitle>{t('Advertisement.AddLink', 'Reklám link hozzáadása')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('Advertisement.AddLinkInstructions', 'Adja meg a YouTube videó linkjét és a videó nevét.')}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('Advertisement.VideoName', 'Videó neve')}
            type="text"
            fullWidth
            variant="outlined"
            value={newYoutubeName}
            onChange={(e) => setNewYoutubeName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('Advertisement.YoutubeLink', 'YouTube Link')}
            type="url"
            fullWidth
            variant="outlined"
            value={newYoutubeLink}
            onChange={(e) => setNewYoutubeLink(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel>{t('Advertisement.Category', 'Kategória')}</InputLabel>
            <Select
              value={newYoutubeCategory}
              onChange={(e) => setNewYoutubeCategory(e.target.value)}
              label={t('Advertisement.Category', 'Kategória')}
            >
              <MenuItem value="LocalProduct">{t('Advertisement.LocalProduct', 'Helyi termék')}</MenuItem>
              <MenuItem value="LocalBusiness">{t('Advertisement.LocalBusiness', 'Helyi vállalkozás')}</MenuItem>
              <MenuItem value="SzeklerlandEvents">{t('Advertisement.SzeklerlandEvents', 'Székelyföldi események')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddYoutube}>{t('Advertisement.Cancel', 'Mégse')}</Button>
          <Button onClick={handleAddYoutube} variant="contained" color="primary">
            {t('Advertisement.Add', 'Hozzáadás')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Map content */}
      <Box sx={{ height: "100%", width: "100%", position: "absolute", top: 0, left: 0, zIndex: 0 }}>
        <div id="map-container" style={{ height: "100%", width: "100%", position: "relative" }}>
            {isMobile ? (
              <MobileDrawer 
                allListings={allListings}
                selectedCategories={selectedCategories}
                selectedKeys={selectedKeys}
                selectedAdmLayers={selectedAdmLayers}
                showCsango={showCsango}
                setShowCsango={setShowCsango}
                handleCategoryToggle={handleCategoryToggle}
                handleKeyToggle={handleKeyToggle}
                handleAdmLayerToggle={handleAdmLayerToggle}
                state={state}
                setFlyToLocation={setFlyToLocation}
                navigate={navigate}
              />
            ) : (
              <>
                {/* Categories*/ }
                {!isDedicatedMap && (
                  <Box className="desktop-category-filter">
                    {Object.entries(categories).filter(([cat]) => cat !== 'Latnivalok').map(([category, keys]) => (
                      <div key={category}>
                        <Typography variant="body2" sx={{ color: 'black', textAlign: 'left' }}>{CategoryLabels[category]}</Typography>
                        {keys.map((key) => (
                          <div key={key}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedKeys[category][key]}
                                  onChange={() => handleKeyToggle(category)(key)}
                                  color="primary"
                                  disabled={!selectedCategories.includes(category)}
                                />
                              }
                              label={<Typography variant="body2" sx={{ color: 'black' }}>{KeyLabels[key]}</Typography>} 
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                  </Box>
                )}

                {/* Sights specific floating box */}
                {!isDedicatedMap && (
                  <Box className="sights-floating-box" sx={{
                      position: 'absolute',
                      bottom: 70,
                      right: 10,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      padding: 2,
                      borderRadius: 2,
                      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
                      zIndex: 1150,
                      minWidth: '8rem',
                      width: 'fit-content',
                      maxWidth: '300px',
                      border: '1px solid #105b79',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end'
                  }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#105b79', textAlign: 'right', width: '100%' }}>
                          {CategoryLabels['Latnivalok']}
                      </Typography>
                      {categories['Latnivalok']?.map((key) => (
                          <div key={key} style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
                              <FormControlLabel
                                  control={
                                      <Checkbox
                                          size="small"
                                          checked={selectedKeys['Latnivalok'][key]}
                                          onChange={() => handleKeyToggle('Latnivalok')(key)}
                                          color="primary"
                                          disabled={!selectedCategories.includes('Latnivalok')}
                                      />
                                  }
                                  label={<Typography variant="body2" sx={{ color: 'black' }}>{KeyLabels[key]}</Typography>}
                                  labelPlacement="start"
                                  sx={{ margin: 0 }}
                              />
                          </div>
                      ))}
                  </Box>
                )}
                {(!isSzallasMap && !isDedicatedMap) && (
                  <Box className="desktop-category-switch">
                    {Object.keys(categories).map((category) => (
                      <div key={category}>
                        <Typography variant="body2" sx={{ color: 'black' }}>{CategoryLabels[category]}</Typography>
                        <Switch
                          checked={selectedCategories.includes(category)}
                          onChange={() => handleCategoryToggle(category)}
                          color="primary"
                        />
                      </div>
                    ))}
                  </Box>
                )}

                {(!isSzallasMap && !isDedicatedMap && Object.keys(admlayers).filter(layer => !['megye', 'kozsegvaros', 'szek', 'helynev', 'folyovizek', 'telepules'].includes(layer)).length > 0) && (
                  <Box className="desktop-admlayer-switch">
                    {Object.keys(admlayers).filter(layer => !['megye', 'kozsegvaros', 'szek', 'helynev', 'folyovizek', 'telepules'].includes(layer)).map((admlayer) => (
                      <div key={admlayer}>
                        <Typography variant="body2" sx={{ color: 'black' }}>{AdmLabels[admlayer]}</Typography>
                        <Switch
                          checked={selectedAdmLayers.includes(admlayer)}
                          onChange={() => handleAdmLayerToggle(admlayer)}
                          color="primary"
                        />
                      </div>
                    ))}
                  </Box>
                )}

                {!isSzallasMap && (
                  <Box className="top-toolbox">
                    {['megye', 'szek', 'kozsegvaros', 'telepules'].map((layer) => (
                      <div key={layer} className="toolbox-item">
                        <Typography variant="body2" sx={{ color: 'black', mr: 1 }}>{AdmLabels[layer]}</Typography>
                        <Switch
                          size="small"
                          checked={selectedAdmLayers.includes(layer)}
                          onChange={() => handleAdmLayerToggle(layer)}
                          color="primary"
                        />
                      </div>
                    ))}
                    <FormControlLabel
                      control={
                        <Checkbox 
                          size="small"
                          checked={showCsango} 
                          onChange={(e) => setShowCsango(e.target.checked)} 
                          sx={{ p: 0.5 }}
                        />
                      }
                      label={t('Map.Toolbox.csango_gis', 'Csángó GIS')}
                      sx={{ m: 0, ml: 1, backgroundColor: 'white', borderRadius: '4px', pr: 1, border: '1px solid #2492be' }}
                    />
                  </Box>
                )}

                {(!isSzallasMap && !isDedicatedMap) && (
                  <Box className="bottom-toolbox">
                    {['helynev', 'folyovizek'].map((layer) => (
                      <div key={layer} className="toolbox-item">
                        <Typography variant="body2" sx={{ color: 'black', mr: 1 }}>{AdmLabels[layer]}</Typography>
                        <Switch
                          size="small"
                          checked={selectedAdmLayers.includes(layer)}
                          onChange={() => handleAdmLayerToggle(layer)}
                          color="primary"
                        />
                      </div>
                    ))}
                  </Box>
                )}
              </>
            )}
            <MapComponent allListings={allListings} allAccommodation={allAccommodation} allMarketplaceAccommodation={allMarketplaceAccommodation} allBuiltHeritage={allBuiltHeritage} 
            allParishes={allParishes} allTelepulesek={allTelepulesek} 
            allCsangoParishes={allCsangoParishes} allCsangoTelepulesek={allCsangoTelepulesek}
            showCsango={showCsango}
            allMegyek={allMegyek} allSzekek={allSzekek} allHelynevek={allHelynevek} allFolyovizek={allFolyovizek}
            allHistory={allHistory} allMineralWater={allMineralWater} allSport={allSport}
            allSportEquipment={allSportEquipment} allOwnProduce={allOwnProduce}
            allTouristRoutes={allTouristRoutes}
            showTouristRoutes={showTouristRoutes}
            setShowTouristRoutes={setShowTouristRoutes}
            selectedAdmLayers={selectedAdmLayers}
            navigate={navigate} dispatch={dispatch} />
            
        </div>
      </Box>
    </Grid>
  </Grid>
</ThemeProvider>
  );}

export default Listings;